/**
 * Column arithmetic for the live screen's panel row.
 *
 * Its own module so `MeetingInsights.tsx` keeps exporting only components:
 * a plain function exported beside them costs that file Fast Refresh
 * (`react-refresh/only-export-components`).
 */

// Width of a CollapsedPanelStrip (Tailwind `w-11` = 2.75rem). A collapsed pane
// leaves this strip in the flex row, so any divider positioned by percentage
// must offset by the strips that precede its boundary.
export const COLLAPSED_STRIP_PX = 44

export interface PanelWidths {
  source: number
  target: number
  insights: number
  batch: number
}

// Percentages of the layout width. `source`/`target` are the Input/Output
// transcript panes, `insights` is the Key Points panel, and `batch` is
// Enhanced Mode. Read whenever a transcript pane is open (width-driven row).
//
// Solved for Input|Output|Enhanced: Input and Output start equal so neither
// header ellipsizes. `source` + `target` come to 62.8 rather than 66.7 because
// Summary and Key Points fold to COLLAPSED_STRIP_PX strips and Enhanced is
// `flex-1`, so those 88px come out of ITS share alone. Solving
// `t·W = 2·(W − t·W − 88)` levels the three (62.1% at 1280px, 63.6% at 1920px).
//
// Named by shape rather than by Mode: no Mode opens on that row any more (both
// now open on the one {@link FOUR_PANEL_WIDTHS} serves), but it is two clicks
// away at any point in a meeting, and these are still its numbers.
//
// Enhanced|Summary|Key Points — Input and Output both folded — does not read
// these at all: it shares the row via equal `analysisFlexGrow` (flex).
export const DEFAULT_PANEL_WIDTHS: PanelWidths = {
  source: 31.4,
  target: 31.4,
  insights: 31.1,
  batch: 31.1,
}

/**
 * Percentages for the row that opens on Input, Output, Summary and Key Points
 * with Enhanced Mode folded away — the layout `meeting` and `interview` now
 * share.
 *
 * {@link DEFAULT_PANEL_WIDTHS} cannot serve this row. There the transcript
 * holds 62.8% and Key Points a further fixed 31.1%, and Summary is the only
 * `flex-1` column, so it is handed what those two leave: a measured 34px of a
 * 1280px row. Those percentages were solved for rows carrying the transcript
 * and ONE analysis pane, which is every row the product had shipped until now.
 *
 * Four columns plus one 44px collapsed strip level out at `i = 25·(W − 44)/W` —
 * 24.14% at 1280px, 24.43% at 1920px — so 24.2 sits between the two. Measured
 * 309.8 | 309.8 | 306.7 | 309.8 at 1280px, and still even at 1024 and 1920.
 */
const FOUR_PANEL_WIDTHS: PanelWidths = {
  source: 24.2,
  target: 24.2,
  insights: 24.2,
  // Enhanced starts collapsed here, so this is only read if the user opens it —
  // at which point the row is one the default was already solved for.
  batch: DEFAULT_PANEL_WIDTHS.batch,
}

/**
 * Column percentages to start the session on, chosen by the panes the screen
 * opens with.
 *
 * Seeded once, at mount: from the first render on, these percentages are the
 * user's — the resize handles write to them — and folding a pane later must not
 * snap their columns back to a default.
 *
 * @param layout Which panes are open on that first render.
 * @returns {@link FOUR_PANEL_WIDTHS} for the transcript-plus-both-analysis-panes
 *   row, otherwise {@link DEFAULT_PANEL_WIDTHS}.
 */
export function initialPanelWidths(layout: {
  sourceOpen: boolean
  targetOpen: boolean
  batchOpen: boolean
  memoOpen: boolean
  keyPointsOpen: boolean
}): PanelWidths {
  // Every clause names part of the shape 24.2 was solved for: four columns and
  // one collapsed strip. Both transcript panes, because one of them folded
  // leaves three columns and two strips — which is the row the 31.4/31.1
  // defaults already solve. Enhanced closed, because opening it makes five
  // columns, where `analysisFlexMode` and `--batch-width` take the arithmetic
  // back over.
  const isFourPanelRow =
    layout.sourceOpen &&
    layout.targetOpen &&
    !layout.batchOpen &&
    layout.memoOpen &&
    layout.keyPointsOpen
  return isFourPanelRow ? FOUR_PANEL_WIDTHS : DEFAULT_PANEL_WIDTHS
}
